import { SolidButton } from "@/components/StoreComponents/StoreButton";

import { Box, styled, useTheme } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";

import { RetailerConnectBenefits } from "./RetailerConnectBenefits";
import { RetailerConnectLogin } from "./RetailerConnectLogin";
import LeavingConfirm from "./LeavingConfirm";
import { ResponsiveDialog } from "@/components/StoreComponents/StoreDialogs/ResponsiveDialog";
import { Retailer } from "@/redux/types";
import { useCheckoutUI } from "../../../../CheckoutUIProvider";
import { CheckoutIntent } from "../../../../types/CheckoutIntent";

enum PopupStatus {
  WELCOME = "welcome",
  CONNECT = "connect",
  LEAVING = "leaving",
}

const Container = styled(motion.div)(({ theme }) => ({
  position: "relative",
  width: "100%",
  overflow: "hidden",
  display: "flex",
  color: theme.palette.primary.main,
}));

const RewardHeadIcon = () => {
  const theme = useTheme();
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="90.453"
      height="70.813"
      viewBox="0 0 90.453 70.813"
      style={{
        stroke: theme.palette.primary.main,
        fill: theme.palette.primary.main,
      }}
    >
      <path
        d="M80.973 32.151a1.5 1.5 0 0 0-1.2-.61h-13.8l5.765-6.516a1.492 1.492 0 0 0 .093-1.875L59.814 6.624A1.5 1.5 0 0 0 58.6 6H13.522a1.5 1.5 0 0 0-1.215.619L.287 23.146a1.49 1.49 0 0 0 .093 1.875l34.556 39.063a1.5 1.5 0 0 0 2.254 0l7.433-8.4 17.335 20.4a1.5 1.5 0 0 0 2.29 0l25.541-30.049a1.483 1.483 0 0 0 .069-1.86zm4.694 11.41H76.2l-5-9.015H79zM42.678 9l7.375 13.522h-27.99L29.439 9zM18.536 25.532l6.591 15.574a1.5 1.5 0 1 0 2.767-1.172l-6.1-14.4h28.488L36.038 57.906l-8.147-17.97a1.5 1.5 0 1 0-2.736 1.241L31.963 56.2 4.838 25.532zm35.031 0h13.712L50.152 44.891l-.009.011L40 56.375zM63.1 69.793l-9.962-23.227h19.9zm13.209-23.227H85.4L67.1 68.093zm-8.538-12.02 5 9.015H55.339l7.975-9.015zm-.107-12.019H53.478L46.1 9h11.731zM14.287 9h11.728l-7.376 13.527H4.453zm36.5 39.7 8.303 19.378-12.471-14.667z"
        transform="translate(.151 -5.9)"
        style={{ strokeWidth: ".2px" }}
        data-name="003---Gems"
      />
    </svg>
  );
};

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 12,
  marginBottom: "20px",
  [theme.breakpoints.down("md")]: {
    marginBottom: "10px",
  },
  ".title": {
    marginTop: 30,
    fontSize: 24,
    fontWeight: 700,
    textAlign: "center",
    [theme.breakpoints.down("md")]: {
      marginTop: 20,
    },
  },
  ".subtitle": {
    fontSize: 16,
    textAlign: "center",
    fontWeight: 500,
    [theme.breakpoints.down("md")]: {
      marginTop: 14,
    },
  },
}));

const EditConnectPopup = ({
  showEdit,
  closeEdit,
  connectReward,
  onSaveCredentials,
  currentUsername,
  currentPassword,
  retailer,
}: {
  showEdit: boolean;
  closeEdit: () => void;
  connectReward: number;
  onSaveCredentials: (username: string, password: string) => Promise<void>;
  currentUsername: string;
  currentPassword: string;
  retailer?: Retailer;
}) => {
  const [state] = useCheckoutUI();
  const { checkoutIntent } = state.context as {
    checkoutIntent: CheckoutIntent;
  };
  const [status, setStatus] = useState<PopupStatus>(PopupStatus.WELCOME);
  const [previousStatus, setPreviousStatus] = useState<PopupStatus>(
    PopupStatus.WELCOME
  );
  const [shouldConfirmLeave, setShouldConfirmLeave] = useState(true);

  useEffect(() => {
    if (
      !checkoutIntent?.retailerConnection?.username ||
      !currentUsername ||
      currentUsername === "" ||
      !currentPassword ||
      currentPassword === ""
    ) {
      setShouldConfirmLeave(true);
    } else {
      setShouldConfirmLeave(false);
    }
  }, [checkoutIntent, showEdit, currentUsername, currentPassword]);

  const onCloseEdit = () => {
    setStatus(PopupStatus.WELCOME);
    setShouldConfirmLeave(false);
    closeEdit();
  };

  const getDialogBody = () => {
    switch (status) {
      case PopupStatus.WELCOME:
        return (
          <Box
            sx={(theme) => ({
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",

              flex: 1,
              marginBottom: "30px",
              [theme.breakpoints.down("md")]: {
                marginBottom: "5px",
              },
            })}
          >
            <HeaderContainer>
              <RewardHeadIcon />
              <div className="title">Exclusive Member Benefits</div>
              <div className="subtitle">
                Learn more about the benefits of using Brandclub
              </div>
            </HeaderContainer>
            <RetailerConnectBenefits
              rewardAmount={connectReward}
              retailerId={retailer?.retailerId}
            />
            <SolidButton
              disableMaxWidth
              sx={(theme) => ({
                marginTop: "16px",
                width: "100%",
                display: "flex",
                flexDirection: "row",
                gap: "8px",
                alignItems: "center",
                maxWidth: 380,
                [theme.breakpoints.up("md")]: {
                  height: 50,
                  fontSize: 16,
                },
              })}
              onClick={() => {
                setStatus(PopupStatus.CONNECT);
              }}
            >
              Continue
            </SolidButton>
          </Box>
        );
      case PopupStatus.CONNECT:
        return (
          <Box sx={{ width: "100%", height: "100%" }}>
            <RetailerConnectLogin
              closeEdit={onCloseEdit}
              onSaveCredentials={onSaveCredentials}
              onChange={(event) => {
                if (
                  event.edited ||
                  !currentUsername ||
                  currentUsername === "" ||
                  !currentPassword ||
                  currentPassword === "" ||
                  event.username !== currentUsername ||
                  event.password !== currentPassword
                ) {
                  setShouldConfirmLeave(true);
                }
              }}
              currentUsername={currentUsername}
              currentPassword={currentPassword}
              retailer={retailer}
            />
          </Box>
        );
      case PopupStatus.LEAVING:
        return (
          <Box sx={{ width: "100%", height: "100%" }}>
            <LeavingConfirm
              closeEdit={onCloseEdit}
              goBack={() => {
                setStatus(previousStatus);
              }}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <ResponsiveDialog
      open={showEdit}
      onClose={
        shouldConfirmLeave
          ? () => {
              setPreviousStatus(status);
              setStatus(PopupStatus.LEAVING);
            }
          : onCloseEdit
      }
    >
      <Container
        sx={(theme) => ({
          [theme.breakpoints.up("md")]: {
            height: 642,
            width: 700,
          },
          overflowY: "auto",
          maxWidth: "100%",
        })}
      >
        <AnimatePresence mode="wait">
          <motion.div
            className="retailer-connect-login-motion-container"
            style={{
              boxSizing: "border-box",
              width: "100%",
              height: "100%",
              display: "flex",
              padding: "24px 24px",
              flexDirection: "column",
            }}
            key={status}
            animate={{ opacity: 1, x: 0 }}
            initial={{ opacity: 0, x: 20 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.15 }}
          >
            {getDialogBody()}
          </motion.div>
        </AnimatePresence>
      </Container>
    </ResponsiveDialog>
  );
};

export default EditConnectPopup;
